"use client";

import React, { useEffect, useRef, useState } from "react";
import { Wallet, Megaphone, Settings, Users, Trophy, ArrowRight, ClipboardCheck } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, []);
  return {
    ref,
    isVisible
  };
}
const timelineSteps = [{
  title: "ADMINISTRATIE",
  description: "Backoffice\xA0 stroomlijnen",
  icon: ClipboardCheck
}, {
  title: "FINANCE",
  description: "signaleert kansen en risico’s op basis van data",
  icon: Wallet
}, {
  title: "MARKETING",
  description: "Zet campagnes uit op basis van finance input",
  icon: Megaphone
}, {
  title: "OPERATIONS",
  description: "vertaalt dit naar de vloer, routing en aanbod",
  icon: Settings
}, {
  title: "HR",
  description: "stuurt op planning, bezetting en loonkosten",
  icon: Users
}, {
  title: "RESULTAAT",
  description: "Meer omzet, hogere marges en meer rust.",
  icon: Trophy
}];
function TimelineItem({
  step,
  index,
  isLast
}: {
  step: (typeof timelineSteps)[0];
  index: number;
  isLast: boolean;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2,
    once: false
  });
  const Icon = step.icon;
  return <div ref={ref} data-index={index} className="flex flex-col items-center flex-1 min-w-[150px] relative group">
      <div className={cn("flex items-center justify-center w-14 h-14 rounded-lg border-2 z-10 transition-all duration-700 ease-out mb-6", isVisible ? index === 5 ? "bg-primary border-primary text-background shadow-xl shadow-[hsl(264_90%_62%)/0.5] scale-110" : "bg-background border-primary text-primary shadow-xl shadow-[hsl(264_90%_62%)/0.5] scale-110" : "bg-muted border-border text-muted-foreground scale-100")}>
        <Icon className="w-6 h-6" />
      </div>

      <div className={cn("w-full transition-all duration-700 ease-out px-2", isVisible ? "opacity-100 translate-y-0" : "opacity-40 translate-y-8")}>
        <Card className={cn("h-[160px] transition-all duration-500 backdrop-blur-xl text-center", isVisible ? "border-primary/60 bg-card/90 shadow-2xl ring-1 ring-white/10" : "border-border bg-card/30")}>
          <CardHeader className="@container/card-header grid auto-rows-min grid-rows-[auto_auto] items-start gap-0.5 has-data-[slot=card-action]:grid-cols-[1fr_auto] [.border-b]:pb-6 ![padding:0px_24px_0px_24px]">
            <CardTitle className="font-default text-lg font-bold tracking-tight text-foreground">
              {step.title}
            </CardTitle>
          </CardHeader>
          <CardContent className="![padding:0px_24px_0px_24px]">
            <p className="text-sm text-muted-foreground font-default leading-relaxed">
              {step.description}
            </p>
          </CardContent>
        </Card>
      </div>

      {!isLast && <div className="hidden lg:flex absolute top-7 -right-4 translate-x-1/2 z-20 text-primary/40">
          <ArrowRight className="w-8 h-8" />
        </div>}
    </div>;
}
export default function SecretosHarineros() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return;
}